import {
  useQuery,
  keepPreviousData,
  useQueryClient,
  useMutation,
} from '@tanstack/vue-query'
import type { PaginationState, SortingState } from '@tanstack/vue-table'
import type {
  Schemas,
  QueryParamsByPath,
  ResponseByPath,
  SchemaAttributes,
  BodyByPath,
  AttributesFor,
} from '@@/api/types.js'
import { useApi } from '@/composables/useApi.js'
import useTranslation from '@/composables/useTranslation.js'
import {
  createRelationshipBuilder,
  many,
  one,
  toPaginationQueryParams,
} from '@/utils/jsonapi.js'
import { sortingStateToQueryParam } from '@/utils/sorting.js'

const PATIENT_LIST_URL = '/{provet_id}/api/patients'
export type PatientListQueryParams = QueryParamsByPath<
  'get',
  typeof PATIENT_LIST_URL
>
export type PatientListResponse = ResponseByPath<'get', typeof PATIENT_LIST_URL>

const PATIENT_DETAIL_URL = '/{provet_id}/api/patients/{id}'
export type PatientDetailQueryParams = QueryParamsByPath<
  'get',
  typeof PATIENT_DETAIL_URL
>
export type PatientDetailResponse = ResponseByPath<
  'get',
  typeof PATIENT_DETAIL_URL
>

const PATIENT_CHECK_DUPLICATE_URL = '/{provet_id}/api/patients/check_duplicate'
export type PatientCheckDuplicateQueryParams = QueryParamsByPath<
  'get',
  typeof PATIENT_CHECK_DUPLICATE_URL
>
export type PatientCheckDuplicateResponse = ResponseByPath<
  'get',
  typeof PATIENT_CHECK_DUPLICATE_URL
>

export type NormalizedPatientMatchData = {
  id: string
  name: string | undefined
  microchip: string | undefined
  species: string | undefined
  breed: string | undefined
  clientId: string | undefined
  clientName: string | undefined
}

const PATIENT_CREATE_URL = '/{provet_id}/api/patients'
export type PatientCreateBody = BodyByPath<'post', typeof PATIENT_CREATE_URL>
export type PatientCreateResponse = ResponseByPath<
  'post',
  typeof PATIENT_CREATE_URL
>

export type NormalizedPatient = Schemas['Patient'] & {
  activeHealthPlans?: Schemas['PatientHealthPlan'][]
}

export type PatientCreate = AttributesFor<PatientCreateBody['data']>

export const patientSex = [
  'UNKNOWN',
  'MALE',
  'FEMALE',
  'MALE_FIXED',
  'FEMALE_FIXED',
  'GELDING',
  'MARE',
  'STALLION',
] as const

export type PatientData = SchemaAttributes<'Patient'>
export type PatientSex = NonNullable<PatientData['gender']>
export type PatientRelationships = {
  clientId: Client['id']
  holdingPlaceNumber?: string
  homeDepartment?: string
  writeOnlyTagTexts?: string[]
  visibleForDepartments?: string[]
  visibleForDepartmentGroups?: string[]
}

/**
 * The list of allowed values for the tab query string parameter
 */
export const patientPageTabs = [
  'details',
  'history_tab',
  'imaging',
  'labs',
  'immunisation',
  'measurements',
  'off_label',
  'diagnoses',
] as const

/**
 * The indexed type of one of the allowed tabs
 */
export type PatientAllowedTab = (typeof patientPageTabs)[number]

export const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (query: MaybeRef<PatientListQueryParams>) =>
    [...patientKeys.lists(), query] as const,
  detail: (id: MaybeRef<string>) => [...patientKeys.all, 'detail', id] as const,
  checkDuplicate: (query: MaybeRef<PatientCheckDuplicateQueryParams>) =>
    [...patientKeys.all, 'checkDuplicate', query] as const,
}

export function usePatientDetailQuery(
  patientId: MaybeRef<string>,
  includes: MaybeRef<string[]>,
  { enabled }: { enabled: MaybeRef<boolean> },
) {
  const api = useApi()
  const route = useRoute()
  const include = unref(includes)

  const query = useQuery({
    queryKey: [...patientKeys.detail(patientId), { include }],
    enabled,
    queryFn: () =>
      api((client) =>
        client.GET(PATIENT_DETAIL_URL, {
          params: {
            path: {
              provet_id: route.params.provetId as string,
              id: unref(patientId),
            },
            query: {
              include,
            },
          },
        }),
      ),
  })

  return reactive(query)
}

export function usePatientListQuery(
  pagination: Ref<PaginationState>,
  sorting: Ref<SortingState>,
) {
  const api = useApi()
  const route = useRoute()

  const queries = computed<PatientListQueryParams>(() => ({
    ...toPaginationQueryParams(pagination.value),
    sort: sortingStateToQueryParam(sorting.value),
  }))

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: patientKeys.list(queries),
    queryFn: async () => {
      const response = await api((client) =>
        client.GET(PATIENT_LIST_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: queries.value,
          },
        }),
      )

      return response
    },
  })

  return reactive(query)
}

function normalizePatientMatchData({
  data,
  included,
}: PatientCheckDuplicateResponse): NormalizedPatientMatchData[] {
  return data.map((patient) => {
    const clientRelData = patient.relationships?.client?.data
    const clientId =
      clientRelData && 'id' in clientRelData
        ? String(clientRelData.id)
        : undefined
    const clientRecord = clientId
      ? included?.find(
          (inc) => inc.type === 'Client' && String(inc.id) === clientId,
        )
      : undefined

    return {
      id: String(patient.id),
      name: patient.attributes?.name ?? undefined,
      microchip: patient.attributes?.microchip ?? undefined,
      species: patient.attributes?.species ?? undefined,
      breed: patient.attributes?.breed ?? undefined,
      clientId,
      clientName: (
        clientRecord?.attributes as Record<string, unknown> | undefined
      )?.full_name as string | undefined,
    }
  })
}

export function usePatientMatchQuery({
  enabled = false,
  filters,
}: {
  enabled?: MaybeRef<boolean>
  filters: MaybeRef<PatientCheckDuplicateQueryParams>
}) {
  const api = useApi()
  const route = useRoute()

  const queries = computed<PatientCheckDuplicateQueryParams>(() => ({
    ...unref(filters),
    include: ['client'],
  }))

  const query = useQuery({
    enabled,
    experimental_prefetchInRender: true,
    queryKey: patientKeys.checkDuplicate(queries),
    queryFn: async () =>
      api((client) =>
        client.GET(PATIENT_CHECK_DUPLICATE_URL, {
          params: {
            path: { provet_id: route.params.provetId as string },
            query: queries.value,
          },
        }),
      ),
    select: (response) => ({
      ...response,
      data: normalizePatientMatchData(response),
    }),
  })

  return reactive(query)
}

export function usePatientCreateMutation() {
  const queryClient = useQueryClient()
  const api = useApi()
  const route = useRoute()

  return useMutation({
    mutationFn: ({
      patient,
      relationships,
    }: {
      patient: PatientCreate
      relationships: PatientRelationships
    }) =>
      api((client) =>
        client.POST(PATIENT_CREATE_URL, {
          params: {
            path: { provet_id: route.params.provetId },
          },
          body: {
            data: {
              type: 'Patient',
              attributes: patient,
              relationships: {
                client: {
                  data: {
                    id: relationships.clientId,
                    type: 'Client' as const,
                  },
                },
                holding_place_number: relationships.holdingPlaceNumber
                  ? {
                      data: {
                        id: relationships.holdingPlaceNumber,
                        type: 'HoldingPlaceNumbers' as const,
                      },
                    }
                  : undefined,
                home_department: relationships.homeDepartment
                  ? {
                      data: {
                        id: relationships.homeDepartment,
                        type: 'Department' as const,
                      },
                    }
                  : undefined,
                visible_departments: relationships.visibleForDepartments?.length
                  ? {
                      data: relationships.visibleForDepartments.map((id) => ({
                        id,
                        type: 'Department' as const,
                      })),
                    }
                  : undefined,
                visible_department_groups: relationships
                  .visibleForDepartmentGroups?.length
                  ? {
                      data: relationships.visibleForDepartmentGroups.map(
                        (id) => ({
                          id,
                          type: 'DepartmentGroup' as const,
                        }),
                      ),
                    }
                  : undefined,
                write_only_tag_texts: relationships.writeOnlyTagTexts?.length
                  ? {
                      data: relationships.writeOnlyTagTexts.map((id) => ({
                        id,
                        type: 'TagText' as const,
                      })),
                    }
                  : undefined,
              },
            },
          },
        }),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() }),
  })
}

const patientRelationships = {
  client: one('Client'),
  cpr_status: one('PatientCPRStatusEntry'),
  active_health_plans: many('PatientHealthPlan'),
  insurance_company: one('ListItem'),
  merge_patient: one('Patient'),
  tags: many('Tag'),
}

export const buildPatientRelationships =
  createRelationshipBuilder(patientRelationships)

export function useEnumSexTranslation() {
  const tEnumSex = useTranslation('common.enums.patientSex')
  const translate = (sex: MaybeRef<PatientSex>) => {
    const key = unref(sex)

    return {
      UNKNOWN: tEnumSex('unknown'),
      MALE: tEnumSex('male'),
      FEMALE: tEnumSex('female'),
      MALE_FIXED: tEnumSex('maleFixed'),
      FEMALE_FIXED: tEnumSex('femaleFixed'),
      GELDING: tEnumSex('gelding'),
      MARE: tEnumSex('mare'),
      STALLION: tEnumSex('stallion'),
    }[key]
  }

  return { translate }
}
